import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/** 사용 가능한 포멧 종류 (필요하면 추가하세요!) */
export const DATE_FORMATS = {
  date: 'YYYY-MM-DD',
  dateMonth: 'YYYY-MM',
  dateTime: 'YYYY-MM-DD HH:mm:ss',
  dateTimeMinute: 'YYYY-MM-DD HH:mm',
  time: 'HH:mm',
} as const;

type DateFormatKey = keyof typeof DATE_FORMATS;

const fmt = (key: DateFormatKey) => DATE_FORMATS[key];

/**  string|Date|Dayjs → string */
export const formatAs = (
  input: string | Date | Dayjs | null,
  key: DateFormatKey,
): string => {
  if (!input) {
    return '';
  }

  const d = dayjs(input);
  return d.isValid() ? d.format(fmt(key)) : '';
};

/**  string|Date|Dayjs → YYYY-MM-DD */
export const formatAsDate = (input: string | Date | Dayjs): string => {
  const d = dayjs(input);
  return d.isValid() ? d.format(fmt('date')) : '';
};

/** 오늘 날짜 포맷 문자열 */
export const getFormattedToday = (key: DateFormatKey = 'date') =>
  dayjs().format(fmt(key));

/** 오늘 날짜 + 지금 시간 포맷 문자열 */
export const getFormattedNow = (key: DateFormatKey = 'dateTime') =>
  dayjs().format(fmt(key));

/** Date → 'date'(기본: YYYY-MM-DD) */
export const formatDateToDateString = (
  date?: Date | null,
  key: DateFormatKey = 'date',
) => (date ? dayjs(date).format(fmt(key)) : '');

/** Dayjs → 'date'(기본: YYYY-MM-DD) */
export const formatDayjsToDateString = (
  date?: Dayjs | null,
  key: DateFormatKey = 'date',
) => (date ? date.format(fmt(key)) : '');

/** 문자열 → Dayjs (엄격 파싱 기본) */
export const parseStringToDayjs = (
  str?: string | null,
  key: DateFormatKey = 'date',
  strict = true,
): Dayjs | null => {
  if (!str) return null;
  const d = dayjs(str, fmt(key), strict);
  return d.isValid() ? d : null;
};

/**  문자열 → 포맷 문자열 */
export const parseStringFormatted = (
  str?: string | null,
  key: DateFormatKey = 'date',
): string => {
  const d = parseStringToDayjs(str, key);
  return d ? d.format(fmt(key)) : '';
};

/** 문자열 → Date */
export const parseStringToDate = (
  str?: string | null,
  key: DateFormatKey = 'date',
  strict = true,
): Date | null => {
  const d = parseStringToDayjs(str, key, strict);
  return d ? d.toDate() : null;
};

/** 문자열 / 날짜 -> 포멧 문자열 */
export const getDateFormat = (dateValue: string | Date): string =>
  dateValue ? formatAs(dateValue, 'dateTime') : '';

export const FormattedNow = getFormattedNow('dateTime');

/** 전화번호 포맷 */
export const getPhoneNumberFormat = (input: string): string => {
  const digits = input.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length === 10)
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11)
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
};
